import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAuthHeaders } from '../utils/auth';
import axios from 'axios';

export interface InvitationCounts {
  trips: number;
  events: number;
  albums: number;
  network: number;
  total: number;
}

/**
 * Get counts of pending invitations by type
 */
export const getPendingInvitationCounts = async (
  userEmail: string,
  userId: string
): Promise<InvitationCounts> => {
  try {
    const [tripCount, eventCount, albumCount, networkCount] = await Promise.all([
      getTripInvitationCount(userEmail),
      getEventInvitationCount(userEmail),
      getAlbumInvitationCount(userEmail),
      getNetworkInvitationCount(userId),
    ]);

    const total = tripCount + eventCount + albumCount + networkCount;

    return {
      trips: tripCount,
      events: eventCount,
      albums: albumCount,
      network: networkCount,
      total,
    };
  } catch (error) {
    console.error('Error fetching invitation counts:', error);
    return {
      trips: 0,
      events: 0,
      albums: 0,
      network: 0,
      total: 0,
    };
  }
};

/**
 * Get count of pending trip invitations
 */
const getTripInvitationCount = async (userEmail: string): Promise<number> => {
  try {
    const invitationsQuery = query(
      collection(db, 'tripInvitations'),
      where('email', '==', userEmail.toLowerCase()),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(invitationsQuery);
    return snapshot.size;
  } catch (error) {
    console.error('Error fetching trip invitation count:', error);
    return 0;
  }
};

/**
 * Get count of pending event invitations
 */
const getEventInvitationCount = async (userEmail: string): Promise<number> => {
  try {
    const invitationsQuery = query(
      collection(db, 'eventInvitations'),
      where('email', '==', userEmail.toLowerCase()),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(invitationsQuery);
    return snapshot.size;
  } catch (error) {
    console.error('Error fetching event invitation count:', error);
    return 0;
  }
};

/**
 * Get count of pending album invitations
 */
const getAlbumInvitationCount = async (userEmail: string): Promise<number> => {
  try {
    const invitationsQuery = query(
      collection(db, 'albumInvitations'),
      where('email', '==', userEmail.toLowerCase()),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(invitationsQuery);
    return snapshot.size;
  } catch (error) {
    console.error('Error fetching album invitation count:', error);
    return 0;
  }
};

/**
 * Get count of pending network invitations
 */
const getNetworkInvitationCount = async (userId: string): Promise<number> => {
  try {
    const headers = await getAuthHeaders();
    const apiUrl = `${process.env.REACT_APP_API_URL}/api/network/invitations/${userId}`;

    const response = await axios.get(apiUrl, { headers });

    if (response.data.success) {
      // Filter for received pending invitations only
      const receivedInvitations = response.data.invitations.filter(
        (inv: any) => inv.direction === 'received' && inv.status === 'pending'
      );
      return receivedInvitations.length;
    }

    return 0;
  } catch (error) {
    console.error('Error fetching network invitation count:', error);
    return 0;
  }
};
